// src/contexts/authContext.js

import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../constants/firebase-config';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../constants/firebase-config';
import { syncClaims, hasClaims } from '../utils/syncClaims';
import { oznaczSesje } from '../utils/znacznikSesji';

// Создаем контекст аутентификации
const AuthContext = createContext();

// Хук для использования контекста аутентификации
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // BEZ setLoading(true) NA KAŻDĄ ZMIANĘ.
      //
      // Provider renderuje `{!loading && children}`, więc podniesienie tej
      // flagi ODMONTOWUJE CAŁĄ APLIKACJĘ. Słuchacz odpala się w chwili
      // `createUserWithEmailAndPassword`, czyli w środku wysyłania formularza
      // rejestracji — i to był ten „biały ekran, a potem znowu pusty
      // formularz": aplikacja znikała, wracała, a trasa nadal wskazywała
      // /sign-up, tyle że stan formularza przepadł. Flaga zostaje wyłącznie
      // na PIERWSZE rozstrzygnięcie sesji przy starcie aplikacji (jej wartość
      // początkowa to `true`), a kolejne zmiany podmieniają już tylko
      // `currentUser` pod działającym drzewem.
      if (user) {
        try {
          // Rola właściciela produktu przychodzi w tokenie, nie w dokumencie:
          // superadmin nie należy do żadnej firmy i dokumentu `users` mieć nie musi.
          const { claims } = await user.getIdTokenResult();
          const superadmin = claims.superadmin === true;

          const userRef = doc(firestore, 'users', user.uid);
          const docSnap = await getDoc(userRef);

          // KONTO W TRAKCIE ZAKŁADANIA — ta tura należy do rejestracji.
          //
          // Dokument users/{uid} tworzy dopiero createStripeCustomer, więc
          // między utworzeniem konta w Auth a odpowiedzią tej funkcji dokumentu
          // NIE MA. Gdyby wołać tu syncMyClaims, nie znalazłby kandydata,
          // oddałby 404 i WYCZYŚCIŁ claimy — czyli ścigałby się z rejestracją,
          // która właśnie próbuje je nadać. Przy okazji z drogi rejestracji
          // znika jedno wywołanie funkcji chmurowej, a to ono najczęściej
          // budzi się zimnym startem.
          if (!docSnap.exists() && !superadmin) {
            setLoading(false);
            return;
          }

          // Права живут в токене и выдаются сервером. Если их там ещё нет,
          // ждём — без них закрыты и расчёты, и файлы. Если есть, панель
          // рисуется сразу, а обновление идёт фоном: роль могли поменять.
          if (await hasClaims(user)) {
            syncClaims(user);
          } else {
            await syncClaims(user);
          }

          // Znacznik dla landingu: strona główna stoi na innym adresie (fleetify.pl),
          // więc sesji Firebase nie widzi. Ciasteczko na domenie nadrzędnej mówi jej
          // tylko tyle, że ktoś jest zalogowany — bez tożsamości i bez tokenu.
          oznaczSesje(true);

          if (docSnap.exists()) {
            setCurrentUser({ ...user, ...docSnap.data(), superadmin });
          } else if (superadmin) {
            // Brak dokumentu NIE MOŻE kasować sesji: wcześniej ta gałąź tylko
            // logowała błąd i zostawiała currentUser jako null, więc właściciel
            // produktu po zalogowaniu lądował z powrotem na ekranie logowania.
            setCurrentUser({ ...user, superadmin, role: 'superadmin' });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        console.log('No user is authenticated'); // Отладка
        oznaczSesje(false);
        setCurrentUser(null);
      }
      setLoading(false);
    });
  
    return () => unsubscribe();
  }, []);
  

  const logout = async () => {
    await signOut(auth);
    oznaczSesje(false);
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading, setCurrentUser, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
